import { useState } from "react";
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl } from "@/services/imageCache/imageCacheService";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  objectFit = "cover",
  priority = false,
  onLoad,
  onError
}: OptimizedImageProps) {
  const [isLoading, setIsLoading] = useState(!priority);
  const [hasError, setHasError] = useState(false);
  
  // Calculate aspect ratio for proper spacing
  const aspectRatio = width / height;
  
  // Get optimized image URL
  const optimizedSrc = src ? getOptimizedImageUrl(src, width, 85, 'webp') : '/placeholder.svg';
  
  return (
    <div 
      className={cn(
        "overflow-hidden",
        className
      )}
      style={{ aspectRatio: aspectRatio }}
    >
      {!hasError ? (
        <img
          src={optimizedSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          className={cn(
            "w-full h-full transition-opacity duration-300",
            objectFit === "cover" && "object-cover",
            objectFit === "contain" && "object-contain",
            objectFit === "fill" && "object-fill",
            objectFit === "none" && "object-none",
            objectFit === "scale-down" && "object-scale-down",
            isLoading ? "opacity-0" : "opacity-100"
          )}
          onLoad={() => {
            setIsLoading(false);
            onLoad?.();
          }}
          onError={() => {
            setHasError(true);
            setIsLoading(false);
            onError?.();
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <span className="text-muted-foreground text-sm">Immagine non disponibile</span>
        </div>
      )}
    </div>
  );
}
